/**
 * SF-4677 — "Eva helpdesk replies" row. SF-5055: moved from the Feature Flags tab to the Help Desk
 * tab, and Eva helpdesk replies need the Premium or Mix help desk plan: on Lite (or no plan) the
 * switch cannot be turned on (sf-api refuses it too, and switches them off on a move to Lite). On
 * Mix Eva runs only for tickets at Premium locations; Lite locations get nothing.
 *
 * A Lite company saved with the switch on (older data) shows it as off in effect, with the same
 * line, and staff can still switch it off; nothing else can be changed until the plan allows it.
 *
 * Three company fields, each saved on its own through the page's saver (one
 * save at a time per company, see companySaveStore):
 *  - `eva_helpdesk_replies_enabled`: the switch. Turning it on asks first. Off, there is no
 *    "Draft a reply with Eva" button and no Eva activity at all.
 *  - `eva_helpdesk_reply_mode` (SF-5081: three levels, each including the one before):
 *    `manual` (staff ask Eva for a draft; Eva does nothing by herself), `draft` (automatic
 *    drafts) or `auto_send` (automatic drafts and replies: Eva emails the lowest-risk replies
 *    herself). Choosing `auto_send` asks first, because Eva will then email customers.
 *    SF-5082: at `auto_send` the level also shows what each priority means for the company and
 *    what Eva may send on her own (EvaAutoSendSettings), each saved as its own field.
 *    Missing = manual, sf-api's default.
 *  - `eva_helpdesk_reply_signoff`: the line Eva signs replies with. Trimmed,
 *    max 120; empty means sf-api's default, "<company name> Customer Care"
 *    cut to 120 characters (shown here the same way).
 *
 * Switching off keeps the saved mode, so switching back on resumes it. The
 * "switch on" question says what the saved level does.
 *
 * Mode and sign-off show only while replies are on in effect (switch on AND the plan allows
 * them). Everyone who can see
 * the tab sees the row; changing anything needs company edit rights. While
 * permissions load the controls are just locked, with no refusal note.
 */
import { useEffect, useState } from 'react';
import { Button, Input, Popconfirm, Radio, Space, Typography } from 'antd';
import { FlagSwitch } from './FlagSwitch';
import { EvaAutoSendSettings } from './EvaAutoSendSettings';
import {
  EVA_REPLIES_NEED_PREMIUM_TEXT,
  evaRepliesPlanAllowed,
  evaRepliesPlanErrorText,
} from './helpDeskPlan';
import type { PendingSave, SaveFieldOptions } from './useSaveCompanyField';
import type { Company } from '../list';

const { Text } = Typography;

type ReplyMode = 'manual' | 'draft' | 'auto_send';

export const SIGNOFF_MAX_LENGTH = 120;

const ON_NOTE_TEXT =
  "Eva writes helpdesk replies from the company's reply playbook, at the level chosen below. Saved immediately.";
const OFF_NOTE_TEXT = 'Eva helpdesk replies are switched off. Switch on to choose a level.';
const OFF_IN_EFFECT_TEXT = `Saved as on, but off in effect. ${EVA_REPLIES_NEED_PREMIUM_TEXT}`;
const MODE_TEXT: Record<ReplyMode, { label: string; description: string }> = {
  manual: {
    label: 'Manual',
    description: 'Staff can ask Eva for a draft on a ticket. Eva does nothing by herself.',
  },
  draft: {
    label: 'Automatic drafts',
    description:
      'Eva drafts a reply on every new ticket and every customer reply. Safety tickets are held for a person.',
  },
  auto_send: {
    label: 'Automatic drafts and replies',
    description:
      'As automatic drafts, and Eva emails the customer herself when a new ticket is safe to answer and its priority, reply type and category are ones she may answer on her own (low priority only, by default). Everything else stays a draft.',
  },
};
// What the "switch on" question says for the saved level.
const SWITCH_ON_TEXT: Record<ReplyMode, string> = {
  manual: 'Staff can ask Eva for a draft reply on a ticket. Eva does nothing by herself.',
  draft: 'Eva will triage new helpdesk tickets for this company and draft replies.',
  auto_send:
    'Eva will triage new helpdesk tickets and email customers directly for eligible replies (low priority only, by default). Everything else stays a draft.',
};
const MODE_SAVED_TEXT: Record<ReplyMode, string> = {
  manual: 'Eva replies set to manual',
  draft: 'Eva replies set to automatic drafts',
  auto_send: 'Eva now sends eligible replies automatically',
};
const MIX_NOTE =
  'On Mix, Eva replies only to tickets at Premium locations; Lite locations get no Eva replies.';
// Lines a description up with its radio's label (the radio dot is 16px plus 8px of space).
const MODE_DESCRIPTION_STYLE = { paddingLeft: 24 };
// A plan refusal from sf-api (the plan changed elsewhere) gets the plain line, not the raw message.
const PLAN_SAVE_OPTIONS: SaveFieldOptions = { errorText: evaRepliesPlanErrorText };

export interface EvaHelpdeskRepliesFlagProps {
  company: Company;
  canEditCompany: boolean;
  /** false when this company is not on the staff member's company list: view only. Defaults to true. */
  companyOnList?: boolean;
  /** The company list lookup failed (unknown, not a refusal): locked, and says it could not check. */
  scopeCheckFailed?: boolean;
  permissionsLoading: boolean;
  saver: {
    save: <K extends keyof Company>(
      field: K,
      value: Company[K],
      successText: string,
      options?: SaveFieldOptions,
    ) => Promise<boolean>;
    pending: PendingSave | null;
    busy: boolean;
  };
}

export const EvaHelpdeskRepliesFlag: React.FC<EvaHelpdeskRepliesFlagProps> = ({
  company,
  canEditCompany,
  companyOnList = true,
  scopeCheckFailed = false,
  permissionsLoading,
  saver,
}) => {
  const pendingOf = (field: keyof Company): unknown =>
    saver.pending?.field === field ? saver.pending.value : undefined;

  const enabled = company.eva_helpdesk_replies_enabled === true;
  const pendingEnabled = pendingOf('eva_helpdesk_replies_enabled');
  const storedMode = company.eva_helpdesk_reply_mode;
  const savedMode: ReplyMode =
    storedMode === 'auto_send' || storedMode === 'draft' ? storedMode : 'manual';
  // Missing plan = Lite (sf-api's default): no Eva helpdesk replies at all.
  const planAllows = evaRepliesPlanAllowed(company.helpDeskPlan);
  // What Eva really does: nothing on Lite, even if the switch is saved on (older data).
  const onInEffect = enabled && planAllows;
  const pendingMode = pendingOf('eva_helpdesk_reply_mode') as ReplyMode | undefined;
  const savedSignOff = company.eva_helpdesk_reply_signoff ?? '';

  const reason = permissionsLoading
    ? null
    : scopeCheckFailed
      ? 'Could not check your company edit access. Try again.'
      : !companyOnList
        ? 'This company is not on your list of companies, so you can only view it.'
        : !canEditCompany
          ? 'Changing this also needs company edit rights (admin:companies:write).'
          : null;
  const locked = permissionsLoading || saver.busy || reason !== null;
  // On Lite the switch can only go off (when older data has it on); it can never go on.
  const planBlocksSwitch = !planAllows && !enabled;
  const planNote = planAllows ? null : enabled ? OFF_IN_EFFECT_TEXT : EVA_REPLIES_NEED_PREMIUM_TEXT;

  // ---- Reply mode: picking automatic asks first ---------------------------
  const [confirmAuto, setConfirmAuto] = useState(false);
  useEffect(() => {
    if (locked) setConfirmAuto(false);
  }, [locked]);
  const pickMode = (next: ReplyMode) => {
    if (locked || next === savedMode) return;
    if (next !== 'manual' && !planAllows) return;
    if (next === 'auto_send') {
      setConfirmAuto(true);
      return;
    }
    void saver.save('eva_helpdesk_reply_mode', next, MODE_SAVED_TEXT[next], PLAN_SAVE_OPTIONS);
  };

  // ---- Sign-off: its own Save button ---------------------------------------
  const [signOff, setSignOff] = useState(savedSignOff);
  // A saved (or re-read) sign-off replaces the box.
  useEffect(() => {
    setSignOff(savedSignOff);
  }, [savedSignOff]);
  const nextSignOff = signOff.trim();
  const signOffUnchanged = nextSignOff === savedSignOff;
  // Same as sf-api: the default is cut to the sign-off limit.
  const defaultSignOff = `${company.name} Customer Care`.slice(0, SIGNOFF_MAX_LENGTH);

  return (
    <Space align="start" data-testid="help-desk-eva-replies" style={{ width: '100%' }}>
      <FlagSwitch
        testId="eva-helpdesk-replies-toggle"
        ariaLabel="Eva helpdesk replies"
        checked={enabled}
        pendingValue={typeof pendingEnabled === 'boolean' ? pendingEnabled : null}
        loading={permissionsLoading}
        disabled={locked || planBlocksSwitch}
        disabledReason={reason ?? (planBlocksSwitch ? EVA_REPLIES_NEED_PREMIUM_TEXT : null)}
        confirmOn={{
          title: 'Switch Eva helpdesk replies on?',
          description: SWITCH_ON_TEXT[savedMode],
          okText: 'Switch on',
        }}
        confirmOff={{
          title: 'Switch Eva helpdesk replies off?',
          description:
            'Eva stops writing replies and staff can no longer ask her for a draft. Replies she already drafted stay on their tickets.',
          okText: 'Switch off',
        }}
        onChange={(next) =>
          void saver.save(
            'eva_helpdesk_replies_enabled',
            next,
            next ? 'Eva helpdesk replies switched on' : 'Eva helpdesk replies switched off',
            PLAN_SAVE_OPTIONS,
          )
        }
      />
      <div>
        <Text strong>Eva helpdesk replies</Text>
        <br />
        <Text type="secondary" data-testid="eva-helpdesk-replies-note">
          {/* The plan line shows for read-only staff too: it says what Eva really does. */}
          {[planNote, reason].filter(Boolean).join(' ') ||
            (onInEffect ? ON_NOTE_TEXT : OFF_NOTE_TEXT)}
        </Text>
        {onInEffect && (
          <Space direction="vertical" size="middle" style={{ marginTop: 12, width: '100%' }}>
            <div data-testid="eva-helpdesk-reply-mode">
              <Text strong>Reply mode</Text>
              <br />
              <Popconfirm
                title="Send replies automatically?"
                description="Eva will email customers directly for eligible tickets (low priority only, by default). Everything else stays a draft."
                okText="Send automatically"
                open={confirmAuto && !locked}
                onOpenChange={(next) => {
                  if (!next) setConfirmAuto(false);
                }}
                onConfirm={() => {
                  setConfirmAuto(false);
                  if (!locked) {
                    void saver.save(
                      'eva_helpdesk_reply_mode',
                      'auto_send',
                      MODE_SAVED_TEXT.auto_send,
                      PLAN_SAVE_OPTIONS,
                    );
                  }
                }}
              >
                <Radio.Group
                  value={pendingMode ?? savedMode}
                  onChange={(e) => pickMode(e.target.value as ReplyMode)}
                  disabled={locked}
                  style={{ marginTop: 4 }}
                >
                  <Space direction="vertical" size={8}>
                    <div data-testid="eva-helpdesk-reply-mode-manual">
                      <Radio value="manual">{MODE_TEXT.manual.label}</Radio>
                      <div style={MODE_DESCRIPTION_STYLE}>
                        <Text type="secondary">{MODE_TEXT.manual.description}</Text>
                      </div>
                    </div>
                    <div data-testid="eva-helpdesk-reply-mode-draft">
                      <Radio value="draft">{MODE_TEXT.draft.label}</Radio>
                      <div style={MODE_DESCRIPTION_STYLE}>
                        <Text type="secondary">{MODE_TEXT.draft.description}</Text>
                      </div>
                    </div>
                    {/* Settings that only apply to this level go in this block, under its description. */}
                    <div data-testid="eva-helpdesk-reply-mode-auto_send">
                      <Radio
                        value="auto_send"
                        // A Radio's own `disabled` wins over the group's, so repeat the lock here.
                        disabled={locked}
                      >
                        {MODE_TEXT.auto_send.label}
                      </Radio>
                      <div style={MODE_DESCRIPTION_STYLE}>
                        <Text type="secondary">{MODE_TEXT.auto_send.description}</Text>
                        {/* SF-5082: only for the saved level, as they only apply to it. */}
                        {savedMode === 'auto_send' && (
                          <EvaAutoSendSettings company={company} locked={locked} saver={saver} />
                        )}
                      </div>
                    </div>
                  </Space>
                </Radio.Group>
              </Popconfirm>
              {company.helpDeskPlan === 'mix' && (
                <div>
                  <Text type="secondary">{MIX_NOTE}</Text>
                </div>
              )}
            </div>
            <div data-testid="eva-helpdesk-reply-signoff">
              <Text strong>Sign-off</Text>
              <Space.Compact style={{ display: 'flex', marginTop: 4, maxWidth: 480 }}>
                <Input
                  aria-label="Sign-off"
                  value={signOff}
                  placeholder={defaultSignOff}
                  maxLength={SIGNOFF_MAX_LENGTH}
                  disabled={locked}
                  onChange={(e) => setSignOff(e.target.value)}
                />
                <Button
                  disabled={locked || signOffUnchanged}
                  loading={pendingOf('eva_helpdesk_reply_signoff') !== undefined}
                  onClick={() =>
                    void saver.save(
                      'eva_helpdesk_reply_signoff',
                      nextSignOff,
                      nextSignOff ? 'Sign-off saved' : 'Sign-off set back to the default',
                    )
                  }
                >
                  Save
                </Button>
              </Space.Compact>
              <Text type="secondary">
                Left empty, Eva signs off as &quot;{defaultSignOff}&quot;.
              </Text>
            </div>
          </Space>
        )}
      </div>
    </Space>
  );
};
